import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import {
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Home, 
  Stethoscope, 
  Building2, 
  Phone,
  Download,
  Share,
  ArrowLeft,
  MapPin,
  ExternalLink
} from "lucide-react";
import { useEffect, useState } from "react";

interface TriageResult {
  session_id: string;
  recommendation: "HOME" | "PCP" | "URGENT_CARE" | "ER_NOW";
  score: number;
  red_flags: boolean;
  explanation: string;
  patient_name?: string;
}

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState<TriageResult | null>(null);
  
  const triageResultData = location.state?.triageResult;
  const sessionId = location.state?.sessionId;

  useEffect(() => {
    if (!triageResultData || !sessionId) {
      navigate("/triage");
      return;
    }

    setResult(triageResultData);
  }, [triageResultData, sessionId, navigate]);

  // Helper functions for display
  const getRecommendationTitle = (recommendation: string) => {
    switch (recommendation) {
      case "HOME": return "Self-Care at Home";
      case "PCP": return "See Your Primary Care Doctor";
      case "URGENT_CARE": return "Visit Urgent Care";
      case "ER_NOW": return "Emergency Care Needed";
      default: return "Care Recommendation";
    }
  };

  const getRecommendationTimeframe = (recommendation: string) => {
    switch (recommendation) {
      case "HOME": return "Monitor for 24-48 hours";
      case "PCP": return "Within 1-2 days";
      case "URGENT_CARE": return "Within 2-4 hours";
      case "ER_NOW": return "Call 911 NOW";
      default: return "As recommended";
    }
  };

  const getRiskLevel = (recommendation: string, redFlags: boolean) => {
    if (redFlags || recommendation === "ER_NOW") return "critical";
    if (recommendation === "URGENT_CARE") return "high";
    if (recommendation === "PCP") return "moderate";
    return "low";
  };

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getRecommendationIcon = () => {
    switch (result.recommendation) {
      case "HOME": return <Home className="h-8 w-8" />;
      case "PCP": return <Stethoscope className="h-8 w-8" />;
      case "URGENT_CARE": return <Building2 className="h-8 w-8" />;
      case "ER_NOW": return <Phone className="h-8 w-8" />;
    }
  };

  const getRiskBadgeVariant = () => {
    const riskLevel = getRiskLevel(result.recommendation, result.red_flags);
    switch (riskLevel) {
      case "low": return "secondary";
      case "moderate": return "default";
      case "high": return "destructive";
      case "critical": return "destructive";
    }
  };

  return (
    <>
      <Helmet>
        <title>Triage Results - AI Symptom Checker | CheckFirstCare</title>
        <meta name="description" content="View your AI-powered healthcare triage results. Get instant recommendations from our clinical decision support tool and emergency room triage app." />
        <link rel="canonical" href="https://www.checkfirstcare.com/results" />
      </Helmet>
      
      <div className="min-h-screen bg-secondary/20 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" size="sm" asChild>
            <Link to="/triage">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Assessment
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Your Health Assessment Results</h1>
            <p className="text-muted-foreground">AI-powered care recommendation based on your symptoms</p>
          </div>
        </div>

        {/* Main Recommendation Card */}
        <Card className="shadow-card-hover mb-8">
          <CardHeader className="text-center pb-4">
            <div className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
              result.recommendation === "HOME" ? "gradient-success" :
              result.recommendation === "PCP" ? "bg-primary" :
              result.recommendation === "URGENT_CARE" ? "bg-warning" :
              "bg-destructive"
            }`}>
              <div className="text-white">
                {getRecommendationIcon()}
              </div>
            </div>
            <CardTitle className="text-2xl lg:text-3xl">
              {getRecommendationTitle(result.recommendation)}
            </CardTitle>
            <div className="flex items-center justify-center space-x-4 mt-4">
              <Badge variant={getRiskBadgeVariant()}>
                {getRiskLevel(result.recommendation, result.red_flags).toUpperCase()} RISK
              </Badge>
              <Badge variant="outline">
                Score: {result.score}
              </Badge>
              {result.red_flags && (
                <Badge variant="destructive">
                  RED FLAGS
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-lg text-muted-foreground">
                <strong>Recommended Timeframe:</strong> {getRecommendationTimeframe(result.recommendation)}
              </p>
            </div>

            {/* Emergency Alert */}
            {result.recommendation === "ER_NOW" && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  <h3 className="font-semibold text-destructive">URGENT: Seek Emergency Care Now</h3>
                </div>
                <div className="space-y-2">
                  <Button variant="destructive" size="lg" className="w-full">
                    <Phone className="mr-2 h-5 w-5" />
                    Call 911 Now
                  </Button>
                  <Button variant="outline" size="lg" className="w-full">
                    <MapPin className="mr-2 h-5 w-5" />
                    Find Nearest Emergency Room
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          
          {/* AI Reasoning */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>AI Analysis</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Medical Assessment:</h4>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {result.explanation}
                  </div>
                </div>
                
                {result.red_flags && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                    <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      <span>Red Flag Symptoms Detected:</span>
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Emergency symptoms have been identified that require immediate medical attention.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-primary" />
                <span>Recommended Actions</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                    1
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {result.recommendation === "ER_NOW" && "Call 911 immediately or go to the nearest emergency room"}
                    {result.recommendation === "URGENT_CARE" && "Visit an urgent care center within 2-4 hours"}
                    {result.recommendation === "PCP" && "Schedule an appointment with your primary care physician within 1-2 days"}
                    {result.recommendation === "HOME" && "Monitor symptoms at home with self-care measures"}
                  </p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                    2
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Monitor your symptoms closely and seek immediate care if they worsen
                  </p>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                    3
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Keep a record of this assessment to share with your healthcare provider
                  </p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-6 space-y-3">
                {result.recommendation === "URGENT_CARE" && (
                  <Button variant="default" size="sm" className="w-full">
                    <MapPin className="mr-2 h-4 w-4" />
                    Find Urgent Care Near Me
                  </Button>
                )}
                {result.recommendation === "PCP" && (
                  <Button variant="default" size="sm" className="w-full">
                    <Phone className="mr-2 h-4 w-4" />
                    Contact My Doctor
                  </Button>
                )}
                {result.recommendation === "HOME" && (
                  <Button variant="default" size="sm" className="w-full">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Self-Care Resources
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Button variant="outline" size="lg">
            <Download className="mr-2 h-5 w-5" />
            Download Results
          </Button>
          <Button variant="outline" size="lg">
            <Share className="mr-2 h-5 w-5" />
            Share with Doctor
          </Button>
          <Button variant="hero" size="lg" asChild>
            <Link to="/triage">
              Take New Assessment
            </Link>
          </Button>
        </div>

        {/* Disclaimer */}
        <Card className="shadow-card bg-muted/50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-warning mt-1" />
              <div className="space-y-2">
                <h4 className="font-medium text-foreground">Important Medical Disclaimer</h4>
                <p className="text-sm text-muted-foreground">
                  This AI assessment is for informational purposes only and is not a substitute for professional medical advice, 
                  diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any 
                  questions you may have regarding a medical condition. If you think you may have a medical emergency, 
                  call your doctor or 911 immediately.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
}